import { AlertTriangle, ListChecks, Info } from 'lucide-react';
import { cn } from '../ui/utils';
import { BandScale } from './BandScale';
import { BandChip } from './BandChip';
import { SubscoreGrid } from './SubscoreGrid';
import { ClinicalInputs } from './ClinicalInputs';
import { TransferCurvePanel } from './TransferCurvePanel';
import { ScoreProvenance } from './ScoreProvenance';
import { OverallProvenance } from './OverallProvenance';
import { RiskTrackPanel } from './RiskTrack';
import { PrognosticIndicesCard } from './PrognosticIndicesCard';
import { GdmtAdvisoryList, readGdmtGaps } from './GdmtAdvisoryList';
import { readProgression } from './clinicalStaging';
import {
  ORGAN_LABEL,
  CONFIDENCE_LABEL,
  bandStyle,
  deriveScoreSteps,
  isRedFlagCapped,
  isInsufficient,
  redFlagLabel,
  scoreFormula,
  scoreReason,
  GRADE_STATE_REASON,
  type OrganScore,
} from './organHealth';

function ConfidenceLine({ level }: { level: OrganScore['confidence'] }) {
  // Secondary metadata: it qualifies the score, it is not a second headline.
  // Demoted in size and colour only — it stays in the accessibility tree,
  // since BandScale's sr-only line covers score and severity but not this.
  return (
    <p className="text-xs text-text-tertiary">
      <span>Confidence</span>{' '}
      <span className="font-medium text-text-secondary">{CONFIDENCE_LABEL[level].replace(' confidence', '')}</span>
    </p>
  );
}

/** Word-form arithmetic for the capped state ("Baseline 25 → ceiling 20"),
 * built only from what the backend actually sent — no fabricated numbers
 * when the pre-cap baseline or ceiling aren't in structured_fields. */
function cappedArithmetic(organ: OrganScore): string | null {
  const base = organ.structured_fields?.base;
  const caps = organ.structured_fields?.caps_applied;
  if (typeof base !== 'number' || !Array.isArray(caps) || caps.length === 0) return null;
  const ceiling = Math.min(...caps);
  return `Baseline ${Math.round(base)} → ceiling ${Math.round(ceiling)}.`;
}

/**
 * Detail view for one organ: patient-agnostic one-liner, large score
 * (never coloured by band), band chip with glyph, confidence as a word (never
 * a colour), the horizontal band scale with a marker, the derivation
 * disclosure, the "to improve / resolve" list, and the persistent advisory
 * footer. Renders a distinct capped-state card (left rule, not a fifth
 * colour) and a low-confidence treatment for the insufficient-data state.
 * Display-only.
 */
export function OrganDetail({
  organ,
  patientLabel,
  /** Defaults ON: a standalone OrganDetail must never show an unqualified
   * score. Containers that already carry the note (OrganHealthPanel) pass
   * false to avoid repeating the same sentence on one screen. */
  showCalibrationNote = true,
}: {
  organ: OrganScore;
  patientLabel?: string;
  showCalibrationNote?: boolean;
}) {
  const style = bandStyle(organ.band);
  const Icon = style.icon;
  const insufficient = isInsufficient(organ);
  const capped = isRedFlagCapped(organ);
  const arithmetic = capped ? cappedArithmetic(organ) : null;
  const isOverall = organ.organ === 'overall';
  const steps = deriveScoreSteps(organ);
  // The "why" line: a scored organ's patient-specific reason, else the honest
  // explanation for a non-scored state (e.g. lung with no spirometry on record),
  // else the generic band meaning.
  const reason = scoreReason(organ) ?? GRADE_STATE_REASON[organ.structured_fields?.reason ?? ''] ?? null;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <Icon className={cn('size-5', style.accent)} aria-hidden="true" />
          <h3 className="text-base font-medium text-foreground">{ORGAN_LABEL[organ.organ]} health</h3>
        </div>
        {patientLabel && <p className="mt-0.5 text-xs text-text-tertiary">{patientLabel}</p>}
      </div>

      {/* The prose read of this row — LLM-composed and audited, or the
          deterministic fallback, always saying which. It RESTATES the stored
          scores rendered below; it never re-derives them, and the provenance
          chip plus the confidence line exist so a clinician can weigh the
          paragraph the way they weigh any consult note: by who wrote it and
          on how much data. */}
      {organ.summary?.text ? (
        <div className="rounded border border-border bg-surface-raised p-4">
          <p className="text-[13px] leading-6 text-text-secondary">{organ.summary.text}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-sunken/40 px-2 py-0.5 text-[10px] font-medium text-text-tertiary"
              title={
                organ.summary.mode === 'llm_v1'
                  ? 'Composed by the clinical language model from the stored scorer result, checked by a deterministic verifier, and passed by an independent audit model.'
                  : 'Composed directly from the stored scorer result without generative rephrasing.'
              }
            >
              {organ.summary.mode === 'llm_v1' ? 'AI-composed · audited' : 'Deterministic'}
            </span>
            {organ.summary.confidence_note ? (
              <span className="text-[11px] leading-4 text-text-tertiary">{organ.summary.confidence_note}</span>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Score + band. Score stays text-primary — never coloured by band. */}
      <div
        className={cn('rounded border bg-surface-raised p-5', capped ? 'border-l-[3px] rounded-l-none' : '', style.surface)}
        style={capped ? { borderLeftColor: 'var(--capped-rule)' } : undefined}
      >
        {/* Header. The score is the focal point: it gets the largest type on
            the card, "/100" is demoted to a unit, and the severity badge is
            baseline-aligned with the number rather than floated against the
            card edge. flex-wrap keeps the badge from colliding with the score
            in a narrow container instead of letting both shrink. */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
          <div className="flex items-baseline gap-1.5">
            <span
              className={cn(
                'text-[2.75rem] font-semibold tabular-nums leading-[1.05] tracking-tight',
                insufficient ? 'text-text-disabled' : 'text-text-primary',
              )}
            >
              {insufficient ? '—' : organ.score}
            </span>
            <span className="text-sm font-medium text-text-tertiary">/100</span>
          </div>
          <BandChip organ={organ} size="md" />
        </div>

        {/* Always-visible, patient-specific reason for THIS score — the "why"
            a clinician reads without expanding the derivation. Falls back to the
            generic band meaning for the Overall roll-up / older rows. */}
        <p className={cn('mt-3 text-sm', reason ? 'text-foreground' : 'text-foreground/80')}>
          {reason ?? style.meaning}
        </p>
        {/* Per-parameter sub-scores (real transfer-curve outputs). Renders
            nothing when the backend sent none, so it's safe to always mount. */}
        <div className="mt-4">
          <SubscoreGrid organ={organ} />
        </div>
        <div className="mt-4">
          {/* No scale without a reading. A full CRITICAL→HEALTHY ramp under an
              em-dash is a severity picture for a severity nobody measured: the
              eye reads the gradient as information and there is none. The
              not-scorable line above already says what is missing. */}
          {typeof organ.score === 'number' ? (
            <BandScale score={organ.score} band={organ.band} />
          ) : null}
        </div>
        <div className="mt-4">
          <ConfidenceLine level={organ.confidence} />
        </div>
        <div className="mt-3">
          {isOverall ? <OverallProvenance organ={organ} /> : <ScoreProvenance steps={steps} formula={scoreFormula(organ)} />}
        </div>
      </div>

      {/* Whole-patient prognostic indices (FIB-4/MELD/KFRE) ride on the roll-up's
          structured_fields. Renders nothing when no calculator had its inputs. */}
      {isOverall && <PrognosticIndicesCard indices={readProgression(organ.structured_fields)} />}

      {/* Advisory, non-directive guideline-therapy prompts (flag-gated on the
          backend; renders nothing until enabled + a gap exists). */}
      {isOverall && <GdmtAdvisoryList gaps={readGdmtGaps(organ.structured_fields)} />}

      {/* The second track, given its own section rather than a line inside the
          function card. The rubric's central claim is that these two scores can
          diverge completely — nesting risk under the function score would frame
          it as a footnote to the number it is meant to be read against. */}
      {!isOverall && (
        <section aria-label="Modifiable risk">
          <h4 className="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-text-tertiary">
            Modifiable risk — future disease of this organ
          </h4>
          <RiskTrackPanel organ={organ} />
        </section>
      )}

      {/* The complete inventory of inputs the score model consumes — captured
          values (with dates + where they landed), inputs that scored but whose
          raw value wasn't stored, and the ones not on record (with the exact
          consequence of their absence) — plus context, conditions, and trends.
          Renders for any scored organ; hides only for the roll-up / no-signal. */}
      <ClinicalInputs organ={organ} />

      {/* Each transfer test plotted on its real curve, with the patient's point
          (value → sub-score). Renders nothing for the roll-up / older scores. */}
      {!isOverall && <TransferCurvePanel organ={organ} />}

      {/* Capped state — structural (left rule), never a fifth colour.
          Test: remove all colour, it must still read via the label + rule. */}
      {capped && (
        <div className="rounded border-l-[3px] rounded-l-none border border-band-critical/30 bg-band-critical-bg p-4" style={{ borderLeftColor: 'var(--capped-rule)' }}>
          <div className="flex items-center gap-2 text-band-critical-text">
            <AlertTriangle className="size-4" aria-hidden="true" />
            <span className="text-xs font-medium uppercase tracking-[0.08em]">Score capped</span>
          </div>
          <ul className="mt-2 space-y-1">
            {organ.red_flags.map((id) => (
              <li key={id} className="text-sm font-medium text-band-critical-text">
                {redFlagLabel(id)}
              </li>
            ))}
          </ul>
          {arithmetic ? <p className="mt-1 text-sm font-medium text-band-critical-text">{arithmetic}</p> : null}
          <p className="mt-2 text-xs text-band-critical-text/90">
            An acute, potentially life-threatening finding sets a hard ceiling on this score — it is not
            averaged against the chronic picture. Assess urgently.
          </p>
        </div>
      )}

      {/* To improve / raise confidence. Low confidence is not an empty state
          — it has a real score and a real band; missing tests degrade
          confidence, never the score. */}
      {organ.to_improve?.length > 0 && (
        <div
          className={cn(
            'rounded border p-4',
            insufficient ? 'border-border bg-surface-sunken' : 'border-border-subtle bg-surface-sunken/40',
          )}
        >
          <div className="flex items-center gap-2 text-foreground">
            <ListChecks className="size-4 text-text-tertiary" aria-hidden="true" />
            <span className="text-xs font-medium uppercase tracking-[0.08em] text-text-tertiary">
              {insufficient ? 'Needed to score' : 'To raise confidence / resolve'}
            </span>
          </div>
          {insufficient ? null : (
            <p className="mt-1.5 text-sm text-text-secondary">
              Score is a best case — the true value can only be lower.
            </p>
          )}
          <ul className="mt-2 space-y-1.5">
            {organ.to_improve.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground/85">
                <span className="mt-1.5 size-1 shrink-0 rounded-full bg-text-tertiary" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Advisory disclaimer + versioned footer — persistent, legible.
          The disclaimer defaults ON so a standalone render is never an
          unqualified score; a container that already states it (OrganHealthPanel,
          which now carries the note above its cards) passes
          showCalibrationNote={false} so the clinician doesn't read the same
          sentence twice on one screen. The versions stay either way — they are
          per-organ and appear nowhere else. */}
      <div className="flex items-start gap-2 rounded border border-border-subtle bg-surface-sunken/40 px-3 py-2.5">
        <Info className="mt-0.5 size-3.5 shrink-0 text-text-tertiary" aria-hidden="true" />
        <p className="text-[11px] leading-relaxed text-text-tertiary">
          {showCalibrationNote && (
            <>Scores are an expert prior; not validated for clinical use. Advisory, non-directive.</>
          )}
          {(organ.rubric_version || organ.method_version) && (
            <span className={cn('font-mono text-text-tertiary/80', showCalibrationNote && 'ml-1')}>
              rubric {organ.rubric_version ?? '—'} · method {organ.method_version ?? organ.structured_fields?.method_version ?? '—'}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
